/**
 * Screen-scoped design tokens for Settings → My Plan → **Checkout**.
 *
 * Lifted verbatim from the design (`Settings.dc.html`, plan checkout section):
 * the order-summary card on the left and the Stripe payment card on the right.
 * Screen-local on purpose — once every screen is re-skinned the shared values
 * get promoted into the MUI theme and this file is deleted.
 */

import {
  contentCard,
  focusRing,
  formField,
  inkfree,
  notification,
  signer,
  switchControl,
  white,
} from "theme/colors";

export const checkoutTokens = {
  color: {
    /** Deep brand ink — headings, line labels, amounts. */
    ink: signer.ink,
    /** Secondary copy — sub-lines, line descriptions. */
    body: signer.text.meta,
    /** Muted copy — "/ month" unit, renewal note, secure footer. */
    muted: signer.text.faint,
    surface: white,
    /** Card chrome. */
    cardBorder: contentCard.borderMuted,
    /** Divider between summary rows. */
    rowDivider: signer.border.divider,
    /** Field / unselected option border. */
    controlBorder: formField.border,
    /** Selected payment option's ring/text — a thin accent, stays orange. */
    accent: signer.orange.main,
    /**
     * Primary action ("Pay") and the selected radio dot — solid fills, so
     * per the standing brand-colour rule these are the app brand red, not
     * `accent` above.
     */
    ctaBg: inkfree.main,
    ctaBgHover: inkfree.dark,
    onAccent: white,
    /** Fill behind the selected payment option. */
    selectedBg: signer.warn.tint,
    /** Unselected radio ring. */
    radioIdle: switchControl.trackOff,
    /** Credit amount, PRIMARY pill, annual-billing note. */
    success: notification.success.icon,
    successBg: notification.success.bg,
    /** Stripe wordmark in "Powered by stripe". */
    stripeBrand: "#635BFF",
    /** Card-brand plate. */
    plateBg: signer.surface.muted,
    plateBorder: contentCard.borderMuted,
    /** Row hover inside the saved-card picker. */
    pickerHoverBg: formField.bg,
    /** Hover wash behind the "Change" link. */
    changeHoverBg: "rgba(225, 80, 15, 0.08)",
    /** Field placeholder (the design's global input rule). */
    placeholder: formField.placeholderMuted,
    focusRing,
  },

  /** Accepted-network chips shown inside the card-number field. */
  networkChip: {
    visa: "#1A1F71",
    mastercard: "#EB001B",
    discover: signer.purple.main,
  },

  font: {
    backLink: "12.5px",
    cardTitle: "15px",
    cardSub: "12.5px",
    rowLabel: "12.5px",
    rowValue: "13px",
    rowUnit: "11px",
    annualNote: "11.5px",
    totalLabel: "13.5px",
    totalValue: "22px",
    renewNote: "11.5px",
    poweredBy: "10.5px",
    cardLine: "12.5px",
    primaryPill: "10px",
    changeLink: "11px",
    brandPlate: "8px",
    optionLabel: "12.5px",
    fieldLabel: "11.5px",
    fieldText: "13px",
    networkChip: "6.5px",
    payButton: "13.5px",
    secureNote: "11px",
  },

  weight: {
    regular: 400,
    field: 500,
    medium: 600,
    strong: 700,
    heading: 800,
  },

  letterSpacing: {
    heading: "-0.01em",
  },

  lineHeight: {
    /** Everything the design leaves at the UA default. */
    tight: "normal",
    renewNote: 1.6,
  },

  radius: {
    card: "14px",
    control: "9px",
    option: "11px",
    picker: "11px",
    annualNote: "8px",
    plate: "4px",
    networkChip: "3px",
    changeHit: "6px",
    pill: "999px",
  },

  border: {
    hairline: "1px",
    emphasis: "1.5px",
  },

  space: {
    /** Vertical rhythm between the back link and the two-column grid. */
    section: "20px",
    columnGap: "20px",
    backLinkGap: "7px",
    cardPaddingY: "24px",
    cardPaddingX: "28px",
    summaryGap: "16px",
    paymentGap: "12px",
    headGap: "3px",
    rowGap: "14px",
    rowY: "11px",
    totalTop: "14px",
    totalBottom: "2px",
    annualNoteY: "9px",
    annualNoteX: "12px",
    annualNoteTop: "8px",
    annualNoteGap: "8px",
    optionY: "12px",
    optionX: "14px",
    optionGap: "10px",
    pickerRowY: "11px",
    pickerRowX: "14px",
    primaryPillY: "2px",
    primaryPillX: "8px",
    changeGap: "5px",
    changeHitY: "4px",
    changeHitX: "6px",
    networkChipGap: "4px",
    fieldGap: "5px",
    fieldRowGap: "10px",
    fieldPaddingX: "12px",
    cardFieldGap: "8px",
    payButtonTop: "4px",
    secureGap: "6px",
  },

  size: {
    /** Fixed width of the payment column; the summary takes the rest. */
    paymentColumn: "420px",
    backIcon: 13,
    backIconStroke: 2.5,
    radio: "16px",
    radioDot: "8px",
    plateWidth: "34px",
    plateHeight: "22px",
    /**
     * Network logo image. The text plate is content-box, so its outer box is
     * `plateWidth/Height` plus its 1px border on each side — the logo matches
     * that outer box so swapping one for the other never moves the row.
     */
    cardLogoWidth: "36px",
    cardLogoHeight: "24px",
    networkChipWidth: "26px",
    networkChipHeight: "17px",
    fieldHeight: "40px",
    zipWidth: "120px",
    payButtonHeight: "44px",
    /** Saved-card dropdown. */
    pickerMaxHeight: "216px",
    pickerOffset: "6px",
    pickerOverhang: "-1.5px",
    pickerZIndex: 20,
    chevron: 10,
    chevronStroke: 2.5,
    pickerCheck: 13,
    pickerCheckStroke: 3,
    annualCheck: 12,
    annualCheckStroke: 2.5,
    lockIcon: 11,
    lockIconStroke: 2,
  },

  shadow: {
    card: "0 1px 3px rgba(33, 21, 71, 0.04)",
    picker: "0 10px 30px rgba(33, 21, 71, 0.15)",
  },
} as const;

export default checkoutTokens;
